import React, { useEffect, useState } from 'react'
import Modal from '../Modal'
import Button from '../Button'
import Typography from '../Typography'
import { showNotification } from '../Notification'
import PTRestAPI from '../../utils/pt_rest_api'
import { useAuth } from '../../utils/platform_auth.jsx'
import articlePreview from './article-preview.jsx'
import CategorySelector from './category-selector.jsx'
import { showPromptDialogAsync } from '../PromptDialog'

const convertToKebabCase = (str) => {
  return str
    .trim() // Remove leading and trailing spaces
    .toLowerCase() // Convert to lowercase
    .replace(/[^a-z0-9]+/g, '-') // Replace all non-alphanumeric characters with a hyphen
    .replace(/^-+|-+$/g, '') // Remove any leading or trailing hyphens
    .replace(/-+/g, '-') // Replace multiple consecutive hyphens with a single hyphen
}

export default ({ controller }) => {
  const [articleID, setArticleID] = useState('')
  const [categoryID, setCategoryID] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [tags, setTags] = useState('')
  const [thumbnail, setThumbnail] = useState(null)
  const [author, setAuthor] = useState('')
  const [errors, setErrors] = useState({})

  const auth = useAuth()
  const { articlePayload, editMode } = controller

  useEffect(() => {
    if (articlePayload) {
      setArticleID(articlePayload.articleID || '')
      setTitle(articlePayload.title || '')
      setDescription(articlePayload.description || '')
      setAuthor(articlePayload.author || '')
      setTags(articlePayload.tags || '')
      setCategoryID(articlePayload.categoryID || '')
    }
  }, [articlePayload, editMode])

  //publishMode is draft or live
  const handleSubmit = async (publishMode) => {
    // Validate fields and submit the form

    if (articleID === '') {
      showNotification({
        message: 'Article ID is required',
        className: 'bg-red-500 text-text_inv',
      })
      return
    }

    if (controller.editMode) {
      const confirm = await showPromptDialogAsync({
        title: 'Edit Mode is On',
        message:
          "You're about to publish the article with contents from VS Code. Do you want to proceed?",
      })
      if (!confirm) return
    }

    //update the payload with the user values only if the have values
    const updatedPayload = {
      articleID: convertToKebabCase(articleID),
      content: editMode ? articlePreview() : articlePayload.content,
      title,
      description,
      author,
      tags,
      thumbnail,
      categoryID,
      status: publishMode === 'live' ? 'published' : 'draft',
    }

    //check if the article exists
    const articleExists = await controller.articleExists(
      updatedPayload.articleID,
    )

    if (articleExists.result) {
      const confirm = await showPromptDialogAsync({
        title: 'Article Update',
        message: 'Article already exists, do you want to update it?',
      })
      if (!confirm) return
    }

    //update the local status
    articlePayload.status = publishMode === 'live' ? 'published' : 'draft'

    //remove the null values
    Object.keys(updatedPayload).forEach((key) => {
      if (!updatedPayload[key]) {
        delete updatedPayload[key]
      }
    })

    const result = await controller.publishArticle(updatedPayload)
    if (result.error) {
      console.log(result.error)
      showNotification({
        message: 'Error publishing article',
        className: 'bg-red-500 text-text_inv',
      })
      return
    }

    controller.setPublishOpen(false)

    //update the url to match the new article
    controller.loadArticle(updatedPayload.articleID)

    showNotification({
      message:
        publishMode === 'live' ? 'Article is now Live!' : 'Draft Published',
      className: 'bg-primary text-text_inv',
    })
  }

  const onCategorySelected = (categories) => {
    setCategoryID(categories.map((cat) => cat.categoryID))
  }

  return (
    <Modal
      isOpen={controller.publishOpen}
      onCloseRequested={controller.setPublishOpen}
      closeOnEscape={true}
      className="w-full sm:w-[500px] md:absolute md:top-[65px] h-full md:h-auto sm:max-w-[700px] bg-paper text-sm sm:text-base z-200"
    >
      <div>
        <Typography variant="h3" className="flex-1 text-text mb-4">
          Publish Article
        </Typography>

        <form>
          <div className="flex flex-row items-center gap-2 mb-4">
            <div className="mb-4 flex-1">
              <label
                className={`block font-bold mb-2 ${
                  errors.articleID ? 'text-error' : 'text-text_secondary'
                }`}
              >
                Article ID
              </label>
              <input
                type="text"
                value={articleID}
                onChange={(e) => setArticleID(e.target.value)}
                className={`outline-none text-text bg-neutral_2 border ${
                  errors.articleID ? 'border-error' : 'border-bordered'
                } rounded p-2 w-full`}
              />
            </div>
            <div className="mb-4 flex-1">
              <label
                className={`block font-bold mb-2 ${
                  errors.author ? 'text-error' : 'text-text_secondary'
                }`}
              >
                Author
              </label>
              <input
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                className={`outline-none text-text bg-neutral_2 border ${
                  errors.author ? 'border-error' : 'border-bordered'
                } rounded p-2 w-full`}
              />
            </div>
          </div>

          {/* Category Selector */}
          <CategorySelector
            controller={controller}
            onSelect={onCategorySelected}
          />
          {/* Title */}
          <div className="mt-4 mb-4">
            <label
              className={`block font-bold mb-2 ${
                errors.title ? 'text-error' : 'text-text_secondary'
              }`}
            >
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`outline-none text-text bg-neutral_2 border ${
                errors.title ? 'border-error' : 'border-bordered'
              } rounded p-2 w-full`}
            />
          </div>

          <div className="mb-4">
            <label
              className={`block font-bold mb-2 ${
                errors.description ? 'text-error' : 'text-text_secondary'
              }`}
            >
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`outline-none text-text bg-neutral_2 border ${
                errors.description ? 'border-error' : 'border-bordered'
              } rounded p-2 w-full`}
              rows="4"
            ></textarea>
          </div>

          {/* Tags */}
          <div className="mb-4">
            <label
              className={`block font-bold mb-2 ${
                errors.tags ? 'text-error' : 'text-text_secondary'
              }`}
            >
              Tags (comma separated)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className={`outline-none text-text bg-neutral_2 border ${
                errors.tags ? 'border-error' : 'border-bordered'
              } rounded p-2 w-full`}
            />
          </div>

          {/* Thumbnail */}

          <div className="mb-4">
            <label
              className={`block font-bold mb-2 ${
                errors.thumbnail ? 'text-error' : 'text-text_secondary'
              }`}
            >
              Thumbnail
            </label>
            <input
              type="file"
              onChange={(e) => setThumbnail(e.target.files[0])}
              className={`outline-none text-text bg-neutral_2 border ${
                errors.thumbnail ? 'border-error' : 'border-bordered'
              } rounded p-2 w-full`}
            />
          </div>

          <div className="mt-5">
            <div className="flex flex-row gap-5">
              <Button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleSubmit('draft')
                }}
                className="w-full bg-red-500 bg-paper font-bold text-white"
              >
                Publish Draft
              </Button>
              <Button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleSubmit('live')
                }}
                variant="action"
                className="w-full"
              >
                Publish Live
              </Button>
            </div>
            {Object.keys(errors).length > 0 && (
              <p className="text-error mb-4 text-center">
                Please correct the highlighted fields above.
              </p>
            )}
          </div>
        </form>
      </div>
    </Modal>
  )
}
